/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { agentCreateSessionHandoff } from "../funcs/agentCreateSessionHandoff.js";
import { agentDeleteAiScanTarget } from "../funcs/agentDeleteAiScanTarget.js";
import { agentGetConfiguration } from "../funcs/agentGetConfiguration.js";
import { agentGetMyConnections } from "../funcs/agentGetMyConnections.js";
import { agentGetPlugins } from "../funcs/agentGetPlugins.js";
import { agentGetSessionMeta } from "../funcs/agentGetSessionMeta.js";
import { agentListAiScanTargets } from "../funcs/agentListAiScanTargets.js";
import { agentListSyncedUsers } from "../funcs/agentListSyncedUsers.js";
import { agentReportAIScan } from "../funcs/agentReportAIScan.js";
import { agentReportSessionMoved } from "../funcs/agentReportSessionMoved.js";
import { agentUpdateConfiguration } from "../funcs/agentUpdateConfiguration.js";
import { agentUpsertAiScanTarget } from "../funcs/agentUpsertAiScanTarget.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AiScanTargetMutationResult } from "../models/components/aiscantargetmutationresult.js";
import { CreateSessionHandoffResult } from "../models/components/createsessionhandoffresult.js";
import { DeleteAiScanTargetResult } from "../models/components/deleteaiscantargetresult.js";
import { DeviceAgentConfiguration } from "../models/components/deviceagentconfiguration.js";
import { GetMyConnectionsResult } from "../models/components/getmyconnectionsresult.js";
import { GetPluginsResult } from "../models/components/getpluginsresult.js";
import { GetSessionMetaResult } from "../models/components/getsessionmetaresult.js";
import { ListAiScanTargetsResult } from "../models/components/listaiscantargetsresult.js";
import { ListSyncedUsersResult } from "../models/components/listsyncedusersresult.js";
import {
  CreateAgentSessionHandoffRequest,
  CreateAgentSessionHandoffSecurity,
} from "../models/operations/createagentsessionhandoff.js";
import {
  DeleteAiScanTargetRequest,
  DeleteAiScanTargetSecurity,
} from "../models/operations/deleteaiscantarget.js";
import {
  GetAgentMyConnectionsRequest,
  GetAgentMyConnectionsSecurity,
} from "../models/operations/getagentmyconnections.js";
import {
  GetAgentPluginsRequest,
  GetAgentPluginsSecurity,
} from "../models/operations/getagentplugins.js";
import {
  GetAgentSessionMetaRequest,
  GetAgentSessionMetaSecurity,
} from "../models/operations/getagentsessionmeta.js";
import {
  GetDeviceAgentConfigurationRequest,
  GetDeviceAgentConfigurationSecurity,
} from "../models/operations/getdeviceagentconfiguration.js";
import {
  ListAiScanTargetsRequest,
  ListAiScanTargetsSecurity,
} from "../models/operations/listaiscantargets.js";
import {
  ListSyncedAgentUsersRequest,
  ListSyncedAgentUsersSecurity,
} from "../models/operations/listsyncedagentusers.js";
import {
  ReportAgentAIScanRequest,
  ReportAgentAIScanSecurity,
} from "../models/operations/reportagentaiscan.js";
import {
  ReportAgentSessionMovedRequest,
  ReportAgentSessionMovedSecurity,
} from "../models/operations/reportagentsessionmoved.js";
import {
  UpdateDeviceAgentConfigurationRequest,
  UpdateDeviceAgentConfigurationSecurity,
} from "../models/operations/updatedeviceagentconfiguration.js";
import {
  UpsertAiScanTargetRequest,
  UpsertAiScanTargetSecurity,
} from "../models/operations/upsertaiscantarget.js";
import { unwrapAsync } from "../types/fp.js";

export class Agent extends ClientSDK {
  /**
   * createSessionHandoff agent
   *
   * @remarks
   * Mint a short-lived capability URL for a rendered session-handoff document (session portability). The device agent uploads the handoff it rendered from the local transcript; the returned URL serves the markdown exactly once (burn-after-read) until expiry, so a cloud agent or another machine can continue the session. Content transits the server only for this purpose and stops being served at first read or expiry, whichever comes first. Requires a per-user key: the fleet-shared org install key is refused because minting a fetch-by-token URL for uploaded content is a per-user, content-bearing surface (the same DNO-383 blast-radius rule as getSessionMeta).
   */
  async createSessionHandoff(
    request: CreateAgentSessionHandoffRequest,
    security?: CreateAgentSessionHandoffSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CreateSessionHandoffResult> {
    return unwrapAsync(agentCreateSessionHandoff(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteAiScanTarget agent
   *
   * @remarks
   * Remove a target the organization added, or clear the row a built-in carries so it returns to having no recorded decision. A built-in itself cannot be removed here; it leaves the list only by leaving Speakeasy's catalog. Requires a session with the org:admin scope.
   */
  async deleteAiScanTarget(
    request: DeleteAiScanTargetRequest,
    security?: DeleteAiScanTargetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeleteAiScanTargetResult> {
    return unwrapAsync(agentDeleteAiScanTarget(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getConfiguration agent
   *
   * @remarks
   * Get the organization-wide device-agent configuration for the dashboard. Requires a session with the org:admin scope. An unconfigured organization returns an empty document with is_configured=false; enrolled agents do not receive a remote layer until an administrator saves one.
   */
  async getConfiguration(
    request?: GetDeviceAgentConfigurationRequest | undefined,
    security?: GetDeviceAgentConfigurationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceAgentConfiguration> {
    return unwrapAsync(agentGetConfiguration(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getMyConnections agent
   *
   * @remarks
   * Report the enrolled user's sign-in state for each MCP server in the plugins delivered to them, with the page that signs in to each. Sign-in belongs to the server, so one sign-in covers every plugin that contains it. Accepts a per-user key, or an org install key with the user's email vouched in the Gram-User-Email header, the same way getPlugins resolves the polling user.
   */
  async getMyConnections(
    request?: GetAgentMyConnectionsRequest | undefined,
    security?: GetAgentMyConnectionsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<GetMyConnectionsResult> {
    return unwrapAsync(agentGetMyConnections(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getPlugins agent
   *
   * @remarks
   * Resolve the marketplaces, plugins, and optional organization configuration assigned to the enrolled user. The device agent reconciles these into the AI developer tools it manages. Organization configuration is delivered on this existing poll so agents do not need a second control-plane request.
   */
  async getPlugins(
    request?: GetAgentPluginsRequest | undefined,
    security?: GetAgentPluginsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<GetPluginsResult> {
    return unwrapAsync(agentGetPlugins(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSessionMeta agent
   *
   * @remarks
   * Resolve display metadata (Speakeasy chat id, generated title, last activity) for captured agent sessions the calling user owns. Used by the device agent's session picker to overlay server-generated titles on locally discovered transcripts; unknown or non-owned session ids are silently omitted, so the picker degrades gracefully. Requires a per-user key: the fleet-shared org install key is refused because session metadata is per-user data.
   */
  async getSessionMeta(
    request: GetAgentSessionMetaRequest,
    security?: GetAgentSessionMetaSecurity | undefined,
    options?: RequestOptions,
  ): Promise<GetSessionMetaResult> {
    return unwrapAsync(agentGetSessionMeta(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listAiScanTargets agent
   *
   * @remarks
   * List the Shadow AI scan targets this organization's device agents probe for: the Speakeasy built-ins plus the organization's own additions, with the catalog version agents echo on scan receipts. Everything listed is probed for; a built-in leaves the list by leaving Speakeasy's catalog, an organization target by being deleted. Requires a session with the org:admin scope.
   */
  async listAiScanTargets(
    request?: ListAiScanTargetsRequest | undefined,
    security?: ListAiScanTargetsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListAiScanTargetsResult> {
    return unwrapAsync(agentListAiScanTargets(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSyncedUsers agent
   *
   * @remarks
   * List users in the current organization who are actively running the Speakeasy device agent, attributed by the email each agent reports on sync. Dashboard-only; requires an org admin session.
   */
  async listSyncedUsers(
    request?: ListSyncedAgentUsersRequest | undefined,
    security?: ListSyncedAgentUsersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListSyncedUsersResult> {
    return unwrapAsync(agentListSyncedUsers(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * reportAIScan agent
   *
   * @remarks
   * Report the result of a device-agent AI scan: which AI tools from the served scan target catalog (or the list embedded in the agent as a fallback) were found installed or running on the device. A scan with zero matches still reports, so organizations can prove a device was scanned and came back clean. Accepts both the per-user key and the org install key (with a vouched email), mirroring getPlugins, because fleet devices must be able to report scans. Fire-and-forget from the agent's perspective: the daemon must never block on this call.
   */
  async reportAIScan(
    request: ReportAgentAIScanRequest,
    security?: ReportAgentAIScanSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(agentReportAIScan(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * reportSessionMoved agent
   *
   * @remarks
   * Record that a captured agent session was moved to another harness on a device (session portability). Carries no session content — only the session identity, the target harness, and device attribution — and lands as a chat_session:move audit event so organizations retain governance visibility over local-first moves. Accepts both the per-user key and the org install key (with a vouched email), mirroring getPlugins, because fleet devices must be able to report moves. Fire-and-forget from the agent's perspective: the daemon must never fail a move because this call failed.
   */
  async reportSessionMoved(
    request: ReportAgentSessionMovedRequest,
    security?: ReportAgentSessionMovedSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(agentReportSessionMoved(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateConfiguration agent
   *
   * @remarks
   * Create or replace the organization-wide, non-secret device-agent configuration. Requires a session with the org:admin scope. Known settings are replaced wholesale — omitting one removes it — while stored keys this server does not recognize are preserved for forward compatibility; identity and credential keys are rejected.
   */
  async updateConfiguration(
    request: UpdateDeviceAgentConfigurationRequest,
    security?: UpdateDeviceAgentConfigurationSecurity | undefined,
    options?: RequestOptions,
  ): Promise<DeviceAgentConfiguration> {
    return unwrapAsync(agentUpdateConfiguration(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * upsertAiScanTarget agent
   *
   * @remarks
   * Add a scan target for this organization or replace one it added earlier. Built-in targets are system-supplied and read-only: a write under a built-in's id is accepted only when it carries that built-in's definition unchanged. Every field is a full replacement except gateway_client, which an existing target keeps when the field is omitted, so a write need not restate the target's matchers; sending gateway_client with empty lists still clears them. Clearing a target's last verifiable matcher also clears any access decision recorded about it, since nothing could enforce it any more; the organization decides again once the target can be recognized at the gateway. Agents pick the change up on their next policy poll. Requires a session with the org:admin scope.
   */
  async upsertAiScanTarget(
    request: UpsertAiScanTargetRequest,
    security?: UpsertAiScanTargetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AiScanTargetMutationResult> {
    return unwrapAsync(agentUpsertAiScanTarget(
      this,
      request,
      security,
      options,
    ));
  }
}
